import { useEffect, useState } from "react"
import api from "../../services/api"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import InitialSetup from "./InitialSetup"
import { useData } from "../../contexts/dataContext"
import Loading from "../layouts/Loading"
import NotFunnel from "./NotFunnel"
import QuickTutorial from "./InitialSetup/QuickTutorial"
import PendingSetup from "./InitialSetup/PendingSetup"
import { useAuth } from "../../contexts/authContext"

const SelectFunnel = () => {
    const { account_id } = useParams()
    const { funnels, lastFunnel, lastFunnelLoaded, funnelsLoaded } = useData()
    const { permissions } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()

    const [state, setState] = useState({
        loading: true,
        setupCompleted: false,
        tutorialCompleted: true,
        notFunnels: false,
        setupData: null
    })
    useEffect(() => {
        if (!account_id || !lastFunnelLoaded || !funnelsLoaded || !Object.keys(permissions || {}).length) return

        api.defaults.headers.common["x-api-key"] = account_id

        init()
    }, [account_id, lastFunnelLoaded, lastFunnel, funnelsLoaded, permissions?.crm, permissions?.settings, permissions?.edit_funnels])

    const canConfigureSetup = permissions?.settings >= 1 && permissions?.edit_funnels >= 1

    const redirectWithQueryStrings = (path) => {
        navigate(`${path}${location.search}`)
    }

    const init = async () => {
        try {
            const setup = await fetchSetup()

            if (!setup?.setupCompleted) {
                return setState(prev => ({
                    ...prev,
                    loading: false,
                    setupCompleted: false,
                    tutorialCompleted: setup?.tutorialCompleted,
                    setupData: setup
                }))
            }

            if (!setup?.tutorialCompleted) {
                return setState(prev => ({
                    ...prev,
                    loading: false,
                    setupCompleted: true,
                    tutorialCompleted: false,
                    setupData: setup
                }))
            }

            if (!funnels.length) {
                return setState(prev => ({
                    ...prev,
                    loading: false,
                    setupCompleted: true,
                    tutorialCompleted: true,
                    notFunnels: true
                }))
            }

            const funnelId = setup?.funnel_id || lastFunnel

            if (funnelId) {
                return redirectWithQueryStrings(`/${account_id}/kanban/${funnelId}`)
            }

            setState(prev => ({
                ...prev,
                loading: false,
                setupCompleted: true,
                tutorialCompleted: true,
                setupData: setup
            }))
        } catch (err) {
            toast.error(err?.response?.data?.message)
            setState(prev => ({
                ...prev,
                loading: false
            }))
        }
    }

    const fetchSetup = async () => {
        const res = await api.get("/accounts/setup")
        return res?.data?.data
    }

    const finishSetup = async () => {
        try {
            await api.patch("/accounts/setup/complete")
            const setup = await fetchSetup()
            toast.success("Configuração inicial concluída! Agora vamos conhecer a plataforma.")
            if (setup?.tutorialCompleted) {
                const funnelId = setup?.funnel_id || lastFunnel
                if (funnelId) {
                    redirectWithQueryStrings(`/${account_id}/kanban/${funnelId}`)
                    return
                }
            }
            setState(prev => ({
                ...prev,
                loading: false,
                setupCompleted: true,
                tutorialCompleted: setup?.tutorialCompleted,
                setupData: setup
            }))
        } catch (err) {
            toast.error(err?.response?.data?.message)
        }
    }

    const finishTutorial = async () => {
        try {
            await api.patch("/accounts/tutorial/complete")
            toast.success("Tutorial concluído! Sua conta está pronta para uso.")
            const funnelId = state.setupData?.funnel_id || lastFunnel
            if (funnelId) {
                redirectWithQueryStrings(`/${account_id}/kanban/${funnelId}`)
                return
            }
            setState(prev => ({ ...prev, tutorialCompleted: true, notFunnels: true }))
        } catch (err) {
            toast.error(err?.response?.data?.message)
        }
    }

    const updateSetupProgress = async (currentStep) => {
        try {
            await api.patch("/accounts/setup/progress", { current_step: currentStep })
            setState(prev => ({
                ...prev,
                setupData: { ...prev.setupData, setupCurrentStep: currentStep }
            }))
            return true
        } catch (err) {
            toast.error(err?.response?.data?.message)
            return false
        }
    }

    const updateTutorialProgress = async ({ completedTasks, currentTask }) => {
        try {
            await api.patch("/accounts/tutorial/progress", {
                completed_tasks: completedTasks,
                current_task: currentTask,
            })
            setState(prev => ({
                ...prev,
                setupData: {
                    ...prev.setupData,
                    tutorialProgress: { completedTasks, currentTask }
                }
            }))
        } catch (err) {
            toast.error(err?.response?.data?.message)
            throw err
        }
    }

    const refreshSetup = () => {
        setState(prev => ({ ...prev, loading: true }))
        init()
    }

    const verifyInitialSetup = async (forceFinish = false) => {
        try {
            const setup = await fetchSetup()

            if (forceFinish) {
                return finishSetup()
            }

            setState(prev => ({
                ...prev,
                setupData: setup
            }))
        } catch (err) {
            toast.error(err?.response?.data?.message)
        }
    }

    if (state.loading) {
        return <Loading fullPage />
    }

    if (state.setupCompleted && state.notFunnels) {
        return <NotFunnel />
    }

    if (!state.setupCompleted) {
        if (!canConfigureSetup) {
            return <PendingSetup onRefresh={refreshSetup} />
        }

        return (
            <InitialSetup
                statusSetup={state.setupData}
                finishSetup={finishSetup}
                updateSetupProgress={updateSetupProgress}
                verifyInitialSetup={verifyInitialSetup}
            />
        )
    }

    if (!state.tutorialCompleted) {
        return (
            <QuickTutorial
                standalone={true}
                funnelId={state.setupData?.funnel_id || lastFunnel}
                initialProgress={state.setupData?.tutorialProgress}
                onProgressChange={updateTutorialProgress}
                nextStep={finishTutorial}
            />
        )
    }

    return null
}

export default SelectFunnel
